/**
 * DEC-001: System View Component
 * 
 * Renders ERPNext meta 1:1 - always shows all fields in meta order.
 * This is the "truth" view for devs/superadmins.
 * 
 * NOTE: This is SystemFormView / SystemDetailView. For list pages,
 * we'll need SystemListView later (out of scope for Phase 1-4).
 */

import React from 'react';
import type { DocTypeSchema } from '@/lib/erp/meta';
import { TabRenderer } from '@/features/inventory/item-detail/components/TabRenderer';
import { buildTabsFromMeta } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { useConnectionsTab, CONNECTIONS_TAB_ID } from '@/features/common/doc-connections/useConnectionsTab';
import { ConnectionsTab } from '@/features/common/doc-connections/ConnectionsTab';
import { useQuickConnectionsTab, QUICK_CONNECTIONS_TAB_ID } from '@/features/common/doc-connections/useQuickConnectionsTab';
import { QuickConnectionsTab } from '@/features/common/doc-connections/QuickConnectionsTab';
import { useDashboardTab, DASHBOARD_TAB_ID } from '@/features/common/doc-dashboard/useDashboardTab';
import { DocDashboardTab } from '@/features/common/doc-dashboard/DocDashboardTab';
import { useItemPriceTab, ITEM_PRICE_TAB_ID } from '@/features/inventory/item-price/useItemPriceTab';
import { ItemPriceTab } from '@/features/inventory/item-price/ItemPriceTab';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import { DynamicViewProvider } from '@/contexts/DynamicViewContext';
import { useLanguage } from '@/i18n/useLanguage';

interface SystemViewProps {
  meta: DocTypeSchema;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  onSave?: () => void;
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  isSaving?: boolean;
  resetKey?: string | number;
  documentName?: string; // Document identifier (name/id) - required for attachment fields
}

export function SystemView({
  meta,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  onSaveSuccess,
  isSaving,
  resetKey,
  documentName,
}: SystemViewProps) {
  const { activeTab, setTabs, resetForScope } = useDetailPageTabs();
  const doctype = meta?.name || '';
  const docName = documentName || formData?.name;
  const { language } = useLanguage();

  // Reset tab state when switching between doctypes/documents.
  React.useEffect(() => {
    const stableDoc = documentName ?? (typeof formData?.name === "string" ? formData.name : "new");
    resetForScope(`${doctype}:${stableDoc || "new"}`);
  }, [doctype, documentName, formData?.name, resetForScope]);
  
  // Register Dashboard tab (automatically adds to tab list, positioned before Inventory)
  useDashboardTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in system view
  });
  
  // Register Connection (Quick Links) tab (automatically adds to tab list, before System Connections)
  useQuickConnectionsTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in system view
  });
  
  // Register Item Price tab (only for Item doctype)
  useItemPriceTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in system view
  });
  
  // Register Connections tab (automatically adds to tab list)
  useConnectionsTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in system view
  });
  
  // System View: raw ERPNext mirror - meta is the single source of truth
  // For now, ignore views.system and _pullFromSystemTab
  // Just use buildTabsFromMeta(meta) to extract all tabs directly from ERPNext Tab Break fields
  // This ensures System View matches ERPNext exactly without any schema interference
  const tabs: TabStructure[] = React.useMemo(() => {
    return buildTabsFromMeta(meta);
  }, [meta]);
  
  // Register meta tabs with context (TabRenderer will also register, but this ensures they're available immediately)
  // This ensures tabs appear in FloatingNav even before TabRenderer processes them
  React.useEffect(() => {
    if (tabs.length > 0) {
      setTabs((prevTabs) => {
        // Preserve special tabs (Dashboard, Connections, Item Price, Quick Connections) that are managed by hooks
        const specialTabIds = new Set([
          DASHBOARD_TAB_ID,
          QUICK_CONNECTIONS_TAB_ID,
          ITEM_PRICE_TAB_ID,
          CONNECTIONS_TAB_ID,
        ]);
        
        const specialTabs = prevTabs.filter(tab => specialTabIds.has(tab.name));
        
        // Merge meta tabs with special tabs
        const mergedTabs = [...tabs];
        
        // Add special tabs that aren't already in tabs
        for (const specialTab of specialTabs) {
          if (!mergedTabs.some(t => t.name === specialTab.name)) {
            mergedTabs.push(specialTab);
          }
        }
        
        // Only update if tab STRUCTURE actually changed (prevents infinite loops).
        // Comparing only tab names is unsafe because many doctypes share tab IDs.
        const signature = (tabsToSign: TabStructure[]) => {
          return tabsToSign.map((t) => {
            const sectionSig = (t.sections || []).map((s) => {
              const fieldnames = (s.columns || [])
                .flatMap((c) => (c.fields || []).map((f) => f.fieldname))
                .slice(0, 25)
                .join(",");
              return `${s.name}:${(s.columns || []).length}:${fieldnames}`;
            }).join("|");
            return `${t.name}:${t.label}:${(t.sections || []).length}:${sectionSig}`;
          }).join("||");
        };

        if (signature(prevTabs) === signature(mergedTabs)) {
          return prevTabs;
        }
        
        return mergedTabs;
      });
    }
  }, [tabs, setTabs]);

  // ✅ SAFE: Keep layout, switch content inside
  // If Dashboard tab is active, render it instead of TabRenderer
  if (activeTab === DASHBOARD_TAB_ID) {
    return (
      <div className="system-view p-6" key={language}>
        <DocDashboardTab 
          doctype={doctype} 
          name={docName || ''} 
          viewMode="system"
          viewStyle={undefined}
        />
      </div>
    );
  }
  
  // If Connection (Quick Links) tab is active, render it instead of TabRenderer
  if (activeTab === QUICK_CONNECTIONS_TAB_ID) {
    return (
      <div className="system-view" key={language}>
        <QuickConnectionsTab doctype={doctype} name={docName || ''} />
      </div>
    );
  }
  
  // If Item Price tab is active, render it instead of TabRenderer
  if (activeTab === ITEM_PRICE_TAB_ID) {
    return (
      <div className="system-view" key={language}>
        <ItemPriceTab doctype={doctype} name={docName || ''} />
      </div>
    );
  }
  
  // If Connections tab is active, render it instead of TabRenderer
  if (activeTab === CONNECTIONS_TAB_ID) {
    return (
      <div className="system-view p-6" key={language}>
        <ConnectionsTab doctype={doctype} name={docName} />
      </div>
    );
  }

  return (
    <div className="system-view" key={language}>
      <DynamicViewProvider
        doctype={doctype}
        mode="systems"
        enableSystemTranslations={false}
      >
        <TabRenderer
          tabs={tabs}
          formData={formData}
          onFieldChange={onFieldChange}
          disabled={disabled}
          onSave={onSave}
          onSubmit={onSubmit}
          onSaveSuccess={onSaveSuccess}
          isSaving={isSaving}
          resetKey={resetKey}
          layoutStyle="grid"
          viewMode="system"
          meta={meta}
          documentName={docName}
        />
      </DynamicViewProvider>
    </div>
  );
}

